<template>
<link v-if="isUmbraco" rel="stylesheet" href="/assets/index.css" />

  <footer :class="gridTemplateColumns">
    <div
      ref="footerColumnRef"
      class="col-start-2 col-span-12 lg:col-start-2 lg:col-span-3"
    >
      <div class="mb-10 text-white">
        <a
          :href="'tel:' + phone"
          class="transition-opacity hover:md:opacity-50 inline-block h4"
        >
          {{ phone }} </a
        ><br />
        <a ref="footerEmailRef" :href="'mailto:' + email" :class="emailClass">
          {{ email }}</a
        >
      </div>
      <div class="hidden lg:block text-white">
        <p class="font-bold mb-4">{{ addressHeader }}</p>
        <div v-html="address"></div>
      </div>
    </div>
    <div class="block mb-10 lg:hidden text-white col-span-6 col-start-2">
      <p class="text-base lg:text-base font-bold mb-4">{{ addressHeader }}</p>
      <div class="[&_p]:text-sm [&_p]:lg:text-base" v-html="address"></div>
    </div>
    <div
      v-for="(item, index) in footerListItems"
      :key="item.title"
      :class="
        'col-span-6 mb-10 lg:col-span-3 text-white lg:col-start-auto ' +
        (index % 2 !== 0 ? 'col-start-2' : '')
      "
    >
      <header3
        :text="item.title"
        :is-umbraco="isUmbraco"
        theme="dark"
        classes="text-sm lg:text-base font-bold mb-2 lg:mb-5 text-white"
      />
      <ul>
        <li v-for="link in item.list" :key="link.ctaLabel" class="mb-2 lg:mb-3">
          <a
            :href="link.ctaLink"
            :class="linkStyles"
            :target="link.target ? link.target : '_self'"
            >{{ link.ctaLabel }}</a
          >
        </li>
      </ul>
    </div>
    <div
      class="xl:mx-12 2xl:mx-20 mt-[100px] lg:mt-[200px] col-span-12 lg:col-span-9 col-start-2 lg:row-start-3 order-last lg:order-none"
    >
      <logo-component :color="svgFill" :subsite="subsite" />
    </div>
    <div
      class="col-span-12 col-start-2 lg:md-0 lg:col-start-auto lg:col-span-3 lg:row-start-3 flex lg:justify-center items-end gap-x-5"
    >
      <span v-for="icon in socialMediaIcons" :key="icon.network">
        <icon-link theme="dark" :url="icon.url" :icon="icon.network" :label="icon.name" />
      </span>
    </div>
  </footer>
</template>

<script lang="ts" setup>
import { ref, toRefs, computed, onMounted } from "vue";
import { IFooterLinkColumnItem, IFooterProps } from "@/types/components/IFooterProps";
import LogoComponent from "@atoms/Logo/Logo.ce.vue";
import Header3 from "@components/Typography/Header3.ce.vue";
import IconLink from "@atoms/IconLink/IconLink.ce.vue";

const props = defineProps<IFooterProps>();
const {
  phone,
  email,
  addressHeader,
  address,
  footerLinkColumns,
  socialMediaLinks,
  subsite,
} = toRefs(props);

const footerColumnRef = ref<HTMLElement | null>(null);
const footerEmailRef = ref<HTMLElement | null>(null);
const isEmailLarger = ref(false);

onMounted(() => {
  const footerColumn = footerColumnRef.value;
  const footerEmail = footerEmailRef.value;

  if (footerColumn && footerEmail) {
    const footerColumnWidth = footerColumn.offsetWidth;
    const footerEmailWidth = footerEmail.offsetWidth;

    isEmailLarger.value = footerEmailWidth > footerColumnWidth;
  }
});

const footerListItems = computed(() => {
  const footerListsArray: IFooterLinkColumnItem[] | null | undefined =
    footerLinkColumns?.value?.items;
  if (!footerListsArray) return;
  const footerListItems = footerListsArray.map((footerListItem) => {
    return {
      title: footerListItem.content.properties.title,
      list: footerListItem.content.properties.footerLinks.map((link) => {
        return {
          ctaLabel: link.title,
          ctaLink: link.url ? link.url : link.route.path,
          target: link.target,
        };
      }),
    };
  });
  return footerListItems;
});

const socialMediaIcons = computed(() => {
  const socialMediaLinksArray = socialMediaLinks?.value ?? [];
  const socialMediaIcons = socialMediaLinksArray.map((socialMediaLink) => {
    return {
      network: socialMediaLink?.network,
      name: socialMediaLink?.name,
      url: socialMediaLink.url,
    };
  });
  return socialMediaIcons;
});

const linkStyles = computed(() => {
  return `transition-opacity hover:md:opacity-50 text-sm lg:text-base`;
});

const svgFill = computed(() => {
  return subsite?.value == "cowiFonden" ? "cowifond-green" : "orange";
});

const gridTemplateColumns = computed(() => {
  let bgColor = subsite?.value == "cowiFonden" ? "bg-ocean" : "bg-aubergine";
  return `col-span-full pt-[60px] pb-8 ${bgColor} w-full h-full grid grid-cols-layout-mobile gap-x-5 md:gap-x-8 
          lg:pt-[100px] lg:pb-12 lg:grid-cols-layout lg:gap-x-12 2xl:gap-x-20`;
});

const emailClass = computed(() => {
  let footerEmail = email?.value;
  if (!footerEmail) return;

  return isEmailLarger.value
    ? "transition-opacity hover:md:opacity-50 inline-block h4 text-sm"
    : "transition-opacity hover:md:opacity-50 inline-block h4";
});
</script>

<style scoped>
.text-red-500 {
  color: red;
}
</style>
